/**
 * Analytics GraphQL Mutations
 *
 * 6 mutations for business intelligence operations:
 * - createAnalytics
 * - recordMetricDataPoint
 * - updateAnalyticsThresholds
 * - setAnalyticsEnabled
 * - recalculateAnalytics
 * - clearAnalyticsData
 *
 * @see graphql_gateway/src/analytics_api/analytics_graphql.rs - Backend implementation
 */

import { gql } from '@apollo/client';
import { ANALYTICS_FIELDS } from './fragments';

/** Create a new analytics metric with backend Snowflake ID */
export const CREATE_ANALYTICS = gql`
  ${ANALYTICS_FIELDS}
  mutation CreateAnalytics($input: CreateAnalyticsInput!) {
    createAnalytics(input: $input) {
      ...AnalyticsFields
    }
  }
`;

/** Record a metric data point (increments counter, updates gauge, etc.) */
export const RECORD_METRIC_DATA_POINT = gql`
  ${ANALYTICS_FIELDS}
  mutation RecordMetricDataPoint($input: RecordMetricDataPointInput!) {
    recordMetricDataPoint(input: $input) {
      ...AnalyticsFields
    }
  }
`;

/** Update analytics thresholds */
export const UPDATE_ANALYTICS_THRESHOLDS = gql`
  ${ANALYTICS_FIELDS}
  mutation UpdateAnalyticsThresholds(
    $analyticsId: String!
    $warningThreshold: Float
    $criticalThreshold: Float
  ) {
    updateAnalyticsThresholds(
      analyticsId: $analyticsId
      warningThreshold: $warningThreshold
      criticalThreshold: $criticalThreshold
    ) {
      ...AnalyticsFields
    }
  }
`;

/** Enable/disable analytics collection */
export const SET_ANALYTICS_ENABLED = gql`
  ${ANALYTICS_FIELDS}
  mutation SetAnalyticsEnabled($analyticsId: String!, $enabled: Boolean!) {
    setAnalyticsEnabled(analyticsId: $analyticsId, enabled: $enabled) {
      ...AnalyticsFields
    }
  }
`;

/** Recalculate analytics (force refresh from source primitives) */
export const RECALCULATE_ANALYTICS = gql`
  ${ANALYTICS_FIELDS}
  mutation RecalculateAnalytics($analyticsId: String!) {
    recalculateAnalytics(analyticsId: $analyticsId) {
      ...AnalyticsFields
    }
  }
`;

/** Clear analytics data (for testing/reset) */
export const CLEAR_ANALYTICS_DATA = gql`
  ${ANALYTICS_FIELDS}
  mutation ClearAnalyticsData($analyticsId: String!) {
    clearAnalyticsData(analyticsId: $analyticsId) {
      ...AnalyticsFields
    }
  }
`;
